import {Box, Typography} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import {humanByteSize, humanCamelCase} from "../../utility/humanReadable";
import React, {createElement, FunctionComponent} from "react";
import {IPreviewProps} from "./preview/Preview";
import {ImagePreview} from "./preview/ImagePreview";
import {AudioPreview} from "./preview/AudioPreview";
import {VideoPreview} from "./preview/VideoPreview";
import {IFileSystemContent} from "./content/FileSystemContent";

interface IMetadataProps {
    label: string
    value?: string | number
}
function Metadata({label, value}: IMetadataProps) {
    return (
        <Box m={1} display="flex" title={value as string}>
            <Typography flexGrow={1} variant="body1">{label}</Typography>
            <Typography variant="body1" sx={{
                maxWidth: "70%",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
            }}>{value|| ""}</Typography>
        </Box>
    );    
}
const previewTypes: IFileType[] = [
    {
        name: "Image",
        contentTypes: ["image/jpeg", "image/png"],
        preview: ImagePreview
    },
    {
        name: "Audio",
        contentTypes: ["audio/mpeg", "audio/x-wav"],
        preview: AudioPreview
    },
    {
        name: "Video",
        contentTypes: ["video/mp4", "video/quicktime"],
        preview: VideoPreview
    }
];

const useScriptStyles = makeStyles(theme => ({
    content: {
        display: "flex",
        flexDirection: "column",
        flexGrow: 1,
        margin: theme.spacing(1),
        width: "30%",
    },
    fileName: {
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    }
}));

interface IFileType {
    name: string
    contentTypes: string[]
    preview: FunctionComponent<IPreviewProps>
}

interface IProps {
    content: IFileSystemContent
    path: string
}
export function AssetMetadata({content, path}: IProps) {
    const classes = useScriptStyles();
    const file = content.getFile(path);
    
    if (! file) return null;

    const previewType = previewTypes.find(type => type.contentTypes.includes(file.contentType));

    return (
        <Box className={classes.content}>
            {file && <Typography className={classes.fileName} title={file.name} variant="h6">{file.name}</Typography>}
            {file && previewType && createElement(previewType.preview, {path: content.getPreview(path)})}
            <Box>
                <Metadata label="Size" value={humanByteSize(file.size as number, true)}/>
                <Metadata label="Created" value={new Date(file.timestamp).toUTCString()}/>
                {Object.keys(file.customMetadata || {}).map(key =>
                    <Metadata key={key} label={humanCamelCase(key)} value={file.customMetadata[key]}/>
                )}
            </Box>
        </Box>
    );
}